import { useEffect, useMemo, useState } from 'react'

import { SectionRowType } from '@pages/WindowsCreateOrEdit/types'

import { useFetchParameterPropertiesQuery } from '@redux/api/parameters.api'
import { useFetchWindowByIdQuery } from '@redux/api/windows.api'

import { useAppSelector, useVariables } from '@hooks'
import { transformCommonParametersToValues } from '@helpers'
import { COMMON_PARAMETERS, ENTITY_TYPES_REQUEST } from '@constants'
import { GETAllVariables } from '@types'

import { EVENT_NAME, LocalEventBus } from '../../../events'

export const useWindow = (id: string, localEventBus: LocalEventBus) => {
  const { data: windowData, isLoading: isLoadingWindowById } = useFetchWindowByIdQuery(id)
  const { data: commonParameters } = useFetchParameterPropertiesQuery({
    code: COMMON_PARAMETERS.STATE,
  })
  const windowVariablesFetch = useVariables({ id, type: ENTITY_TYPES_REQUEST.WINDOW })

  const [windowVariables, setWindowVariables] = useState<GETAllVariables>([])

  useEffect(() => {
    if (windowVariablesFetch && windowVariablesFetch.length > 0) {
      setWindowVariables(windowVariablesFetch)
    }
  }, [windowVariablesFetch])

  useEffect(() => {
    const handleChangeVariable = (event: CustomEvent) => {
      const newVariable = event.detail

      setWindowVariables(prevVariables => {
        const newWindowVariables = [...prevVariables]

        const foundIndex = newWindowVariables.findIndex(variable => {
          return variable.internalId === newVariable.internalId
        })

        if (foundIndex != -1) {
          newWindowVariables[foundIndex] = newVariable
        }

        return newWindowVariables
      })
    }

    localEventBus.subscribe(EVENT_NAME.CHANGE_VARIABLE_VALUE, handleChangeVariable)

    return () => {
      return localEventBus.unsubscribe(EVENT_NAME.CHANGE_VARIABLE_VALUE, handleChangeVariable)
    }
  }, [])

  const { dialogWindows, selectedId } = useAppSelector(state => state.dialogWindowManager)
  const currentDialogWindow = dialogWindows.find(dialogWindow => dialogWindow.id === selectedId)

  const initialCommonParameters = useMemo(
    () => transformCommonParametersToValues(commonParameters),
    [commonParameters]
  )

  const sectionRows = useMemo(() => {
    if (windowData?.configs) {
      return (
        windowData?.configs?.reduce((acc, item) => {
          const lineIndex = acc.findIndex(row => row.line === item.line)
          let currentLine = lineIndex >= 0 ? acc[lineIndex] : null

          if (!currentLine) {
            currentLine = { line: item.line, columns: [] }

            acc.push(currentLine)
          }

          currentLine.columns[item.column] = {
            ...item,
            tabs: windowData.sections?.find(section => section.id === item.sectionId)?.tabs ?? [],
          }

          return acc
        }, [] as SectionRowType[]) || []
      )
    }

    return []
  }, [windowData])

  return {
    data: {
      sectionRows,
      initialCommonParameters,
      windowVariables,
    },
    state: {
      currentDialogWindow,
      dialogWindows,
      isLoadingWindowById,
    },
    handlers: {},
  }
}
